import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import ProfileSettingsModal from '../components/ProfileSettingsModal';
import {
  UserCircleIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  PhoneIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  BriefcaseIcon,
  VideoCameraIcon,
  ClockIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const Profile = () => {
  const { currentUser, userRole, isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    bio: '',
    telephone: '',
    platform: '',
    status: ''
  });

  // Fetch user data from Firestore
  const fetchUserData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(data);
        setFormData({
          name: data.name || '',
          email: data.email || currentUser.email || '',
          role: userRole || '',
          bio: data.bio || '',
          telephone: data.telephone || '',
          platform: data.platform || '',
          status: data.status || ''
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load profile data');
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser, userRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        name: formData.name,
        bio: formData.bio,
        telephone: formData.telephone,
        platform: formData.platform,
        status: formData.status,
        updatedAt: new Date()
      });

      // Refresh user data after update
      await fetchUserData();
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        email: userData.email || currentUser?.email || '',
        role: userRole || '',
        bio: userData.bio || '',
        telephone: userData.telephone || '',
        platform: userData.platform || '',
        status: userData.status || ''
      });
    }
    setIsEditing(false);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'author':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return '👑';
      case 'author':
        return '✍️';
      default:
        return '👤';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="mt-2 text-gray-600">
            Manage your account information and preferences
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white shadow rounded-lg">
          {/* Profile Header */}
          <div className="px-6 py-8 border-b border-gray-200">
            <div className="flex items-center space-x-6">
              <div className="flex-shrink-0">
                {userData?.photoURL ? (
                  <img 
                    src={userData.photoURL} 
                    alt="Profile" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <UserCircleIcon className="w-16 h-16 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {userData?.name || currentUser?.displayName || 'User Profile'}
                </h2>
                {userData?.bio && (
                  <p className="text-gray-600 mt-2 italic">"{userData.bio}"</p>
                )}
                <p className="text-gray-600 mt-1">
                  {isAdmin ? 'Administrator' : 'Author'} Account
                </p>
                <div className="mt-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(userRole)}`}>
                    <span className="mr-2">{getRoleIcon(userRole)}</span>
                    {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'User'}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PencilIcon className="w-4 h-4 mr-2" />
                    Edit Profile
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      <XMarkIcon className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="px-6 py-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your full name"
                  />
                ) : (
                  <div className="flex items-center p-3 bg-gray-50 rounded-md">
                    <UserCircleIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-gray-900">
                      {userData?.name || currentUser?.displayName || 'Not set'}
                    </span>
                  </div>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="flex items-center p-3 bg-gray-50 rounded-md">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <span className="text-gray-900">{userData?.email || currentUser?.email}</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Email cannot be changed
                </p>
              </div>

              {/* Bio Field */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                {isEditing ? (
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Tell us about yourself..."
                  />
                ) : (
                  <div className="flex items-start p-3 bg-gray-50 rounded-md">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                    <span className="text-gray-900">
                      {userData?.bio || 'No bio added yet'}
                    </span>
                  </div>
                )}
              </div>

              {/* Telephone Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <div className="flex items-center p-3 bg-gray-50 rounded-md">
                    <PhoneIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-gray-900">
                      {userData?.telephone || 'Not provided'}
                    </span>
                  </div>
                )}
              </div>

              {/* Role Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Role
                </label>
                <div className="flex items-center p-3 bg-gray-50 rounded-md">
                  <ShieldCheckIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <span className="text-gray-900">
                    {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'User'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Role is managed by administrators
                </p>
              </div>

              {/* Platform Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform
                </label>
                {isEditing ? (
                  <select
                    name="platform"
                    value={formData.platform}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select platform</option>
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                    <option value="web">Web</option>
                    <option value="desktop">Desktop</option>
                  </select>
                ) : (
                  <div className="flex items-center p-3 bg-gray-50 rounded-md">
                    <GlobeAltIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className="text-gray-900">
                      {userData?.platform ? userData.platform.charAt(0).toUpperCase() + userData.platform.slice(1) : 'Not specified'}
                    </span>
                  </div>
                )}
              </div>

              {/* Status Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                {isEditing ? (
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select status</option>
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                    <option value="Busy">Busy</option>
                    <option value="Away">Away</option>
                  </select>
                ) : (
                  <div className="flex items-center p-3 bg-gray-50 rounded-md">
                    <ClockIcon className="w-5 h-5 text-gray-400 mr-3" />
                    <span className={`font-medium ${
                      userData?.status === 'Online' ? 'text-green-600' : 
                      userData?.status === 'Offline' ? 'text-gray-600' : 
                      userData?.status === 'Busy' ? 'text-red-600' : 
                      userData?.status === 'Away' ? 'text-yellow-600' : 'text-gray-600'
                    }`}>
                      {userData?.status || 'Unknown'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg">
            <div className="text-sm text-gray-600">
              <p>
                <strong>Member since:</strong> {currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'Unknown'}
              </p>
              <p className="mt-1">
                <strong>Last sign in:</strong> {currentUser?.metadata?.lastSignInTime ? new Date(currentUser.metadata.lastSignInTime).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        {userData && (
          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Statistics & Information</h3>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {/* Earnings */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="w-8 h-8 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Earnings</p>
                      <p className="text-2xl font-bold text-green-800">
                        ${userData.earnings ? userData.earnings.toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total References */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                  <div className="flex items-center">
                    <UserGroupIcon className="w-8 h-8 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total References</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {userData.totalrefs || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Work */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
                  <div className="flex items-center">
                    <BriefcaseIcon className="w-8 h-8 text-purple-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-purple-600">Total Work</p>
                      <p className="text-2xl font-bold text-purple-800">
                        {userData.totalw || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Zoom ID */}
                {userData.zoomId && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg">
                      <div className="flex items-start">
                        <VideoCameraIcon className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-orange-600 mb-2">Zoom Meeting Info</p>
                          <p className="text-sm text-orange-800 break-words">
                            {userData.zoomId}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Under Reference */}
                {userData.underef && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg">
                      <div className="flex items-start">
                        <MapPinIcon className="w-6 h-6 text-gray-600 mr-3 mt-1" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-600 mb-2">Under Reference</p>
                          <p className="text-sm text-gray-800 font-mono">
                            {userData.underef}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security Section */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Security & Privacy</h3>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Two-Factor Authentication</h4>
                  <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                </div>
                <button className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-500">
                  Enable
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Password</h4>
                  <p className="text-sm text-gray-500">Change your password regularly</p>
                </div>
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Change
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Settings Modal */}
        <ProfileSettingsModal 
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
        />
      </div>
    </div>
  );
};

export default Profile;
